import { useEffect, useRef, useState } from "react";

/**
 * Thumb — stable image renderer that eliminates the "old cached image flash"
 * seen when a new article card mounts. Strategy:
 *  1. Neutral background stays visible until the image is actually loaded.
 *  2. Fade in on onLoad — AND also check `complete && naturalWidth > 0` on
 *     mount / when src changes, so browser-cached images (very common when
 *     the same thumb was just seen in a list) never stay stuck at opacity 0.
 *  3. onError → keep the neutral background (no broken-image icon).
 */
export default function Thumb({ src, alt = "", className = "", fit = "contain", loading = "lazy", ...rest }) {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    setLoaded(false);
    setFailed(false);
    const el = imgRef.current;
    if (el && el.complete && el.naturalWidth > 0) {
      setLoaded(true);
    }
  }, [src]);

  if (!src || failed) {
    return <div className={`w-full h-full bg-[#0A0A0A] ${className}`} aria-label={alt} />;
  }
  const fitCls = fit === "cover" ? "object-cover" : "object-contain";
  return (
    <img
      ref={imgRef}
      key={src}
      src={src}
      alt={alt}
      loading={loading}
      onLoad={() => setLoaded(true)}
      onError={() => setFailed(true)}
      className={`w-full h-full ${fitCls} transition-opacity duration-300 ${loaded ? "opacity-100" : "opacity-0"} ${className}`}
      style={{ background: "#0A0A0A" }}
      {...rest}
    />
  );
}
